// Window manager for the in-app desktop windowing system (ported from the /dev
// desktop preview). Windows render real route content via a per-window
// MemoryRouter (see components/desktop/WindowContent) — NOT iframes — so no
// taskbar/top-bar chrome appears inside a window (MainLayout suppresses chrome
// when the WindowedContext flag is set).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface WindowApp {
  /** Route the window renders, e.g. "/jobs" or "/clients/5/jobs" */
  route: string;
  title: string;
  /** Material Symbols icon name */
  icon: string;
  /** Cleanup when the window closes. Not persisted across reloads. */
  onClose?: () => void;
  /** Render the route in an iframe instead of in-process (#127) — used by the
   *  portal preview, which needs its own auth realm. */
  iframe?: boolean;
}

export interface WinState extends WindowApp {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minimized: boolean;
  maximized: boolean;
  /** Pinned windows always render above non-pinned ones (always-on-top). */
  pinned: boolean;
  zIndex: number;
}

interface WindowManagerValue {
  windows: WinState[];
  activeId: string | null;
  openWindow: (app: WindowApp) => void;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  toggleMaximize: (id: string) => void;
  togglePin: (id: string) => void;
  restoreWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  setWindowBounds: (id: string, bounds: Partial<Pick<WinState, 'x' | 'y' | 'w' | 'h'>>) => void;
  setWindowTitle: (id: string, title: string) => void;
  /** #187 tiling: snap one window to the left/right half of the layer. */
  snapWindow: (id: string, side: 'left' | 'right') => void;
  /** #187 tiling: arrange all non-minimized windows in an even grid. */
  tileWindows: () => void;
}

const WindowManagerContext = createContext<WindowManagerValue | null>(null);

/** True when the current React tree is rendering inside a desktop window. */
export const WindowedContext = createContext(false);
export const useWindowed = () => useContext(WindowedContext);

const BASE_Z = 10;
export const MIN_W = 360;
export const MIN_H = 240;

// Layout persistence (#75): windows survive a reload. Bounds are clamped back
// on-screen at restore in case the viewport shrank since they were saved.
const LS_WINDOWS = 'wm_windows_v1';

const loadSaved = (): WinState[] => {
  try {
    const raw = localStorage.getItem(LS_WINDOWS);
    if (!raw) return [];
    const list = JSON.parse(raw) as WinState[];
    if (!Array.isArray(list)) return [];
    return list
      .filter(w => w && typeof w.route === 'string' && typeof w.id === 'string')
      // Portal-preview windows (#127) depend on a live token swap and an
      // onClose that JSON can't persist — never restore one after a reload.
      .filter(w => !w.route.startsWith('/portal/'))
      .slice(0, 12)
      .map(w => ({
        ...w,
        x: Math.min(Math.max(0, Number(w.x) || 0), Math.max(0, window.innerWidth - 160)),
        y: Math.min(Math.max(0, Number(w.y) || 0), Math.max(0, window.innerHeight - 160)),
        w: Math.max(MIN_W, Math.min(Number(w.w) || MIN_W, window.innerWidth)),
        h: Math.max(MIN_H, Math.min(Number(w.h) || MIN_H, window.innerHeight)),
        pinned: !!w.pinned,
        minimized: !!w.minimized,
        maximized: !!w.maximized,
        zIndex: Number(w.zIndex) || BASE_Z,
      }));
  } catch { return []; }
};

// Touch-optimized default (#75): on phones, windows open maximized — moving
// 360px floats around a 390px screen is not a usable interaction.
const isPhone = () => window.innerWidth < 768;

export const WindowManagerProvider = ({ children }: { children: ReactNode }) => {
  const [windows, setWindows] = useState<WinState[]>(loadSaved);
  const [activeId, setActiveId] = useState<string | null>(null);
  const zCounter = useRef(Math.max(BASE_Z, ...loadSaved().map(w => w.zIndex)) + 1);
  const spawnCounter = useRef(0);

  // Mirror of `windows` for callbacks that must not close over stale state.
  const windowsRef = useRef<WinState[]>(windows);
  useEffect(() => { windowsRef.current = windows; }, [windows]);

  // Persist layout on every change (cheap: ≤12 small objects).
  useEffect(() => {
    try { localStorage.setItem(LS_WINDOWS, JSON.stringify(windows)); } catch { /* quota */ }
  }, [windows]);

  const openWindow = useCallback((app: WindowApp) => {
    const n = spawnCounter.current++;
    const id = `win-${Date.now()}-${n}`;
    // Cascade new windows; keep them clear of the taskbar and screen edges.
    const offset = (n % 8) * 28;
    const w = Math.min(980, Math.max(MIN_W, window.innerWidth - 160));
    const h = Math.min(640, Math.max(MIN_H, window.innerHeight - 200));
    setWindows(ws => [
      ...ws,
      {
        ...app,
        id,
        x: 60 + offset,
        y: 16 + offset,
        w,
        h,
        minimized: false,
        maximized: isPhone(),
        pinned: false,
        zIndex: ++zCounter.current,
      },
    ]);
    setActiveId(id);
  }, []);

  // onClose fires OUTSIDE the updater — updaters must be pure (React can call
  // them twice), and a double token-restore would be a real bug.
  const closeWindow = useCallback((id: string) => {
    windowsRef.current.find(w => w.id === id)?.onClose?.();
    setWindows(ws => ws.filter(w => w.id !== id));
    setActiveId(cur => (cur === id ? null : cur));
  }, []);

  const minimizeWindow = useCallback((id: string) => {
    setWindows(ws => ws.map(w => (w.id === id ? { ...w, minimized: true } : w)));
    setActiveId(cur => (cur === id ? null : cur));
  }, []);

  const toggleMaximize = useCallback((id: string) => {
    setWindows(ws => ws.map(w => (w.id === id ? { ...w, maximized: !w.maximized } : w)));
  }, []);

  const togglePin = useCallback((id: string) => {
    setWindows(ws =>
      ws.map(w => (w.id === id ? { ...w, pinned: !w.pinned, zIndex: ++zCounter.current } : w)));
  }, []);

  const focusWindow = useCallback((id: string) => {
    setWindows(ws => ws.map(w => (w.id === id ? { ...w, zIndex: ++zCounter.current } : w)));
    setActiveId(id);
  }, []);

  const restoreWindow = useCallback((id: string) => {
    setWindows(ws =>
      ws.map(w => (w.id === id ? { ...w, minimized: false, zIndex: ++zCounter.current } : w))
    );
    setActiveId(id);
  }, []);

  const setWindowBounds = useCallback(
    (id: string, bounds: Partial<Pick<WinState, 'x' | 'y' | 'w' | 'h'>>) => {
      setWindows(ws => ws.map(w => (w.id === id ? { ...w, ...bounds } : w)));
    },
    []
  );

  const setWindowTitle = useCallback((id: string, title: string) => {
    setWindows(ws => ws.map(w => (w.id === id && w.title !== title ? { ...w, title } : w)));
  }, []);

  // The window layer sits below the taskbar (top-12 on md+, top-14 on phones);
  // coordinates are layer-relative, so tiling only needs the layer's size.
  const layerSize = () => ({
    w: window.innerWidth,
    h: Math.max(MIN_H, window.innerHeight - (window.innerWidth >= 768 ? 48 : 116)),
  });

  const snapWindow = useCallback((id: string, side: 'left' | 'right') => {
    const { w: lw, h: lh } = layerSize();
    const half = Math.floor(lw / 2);
    setWindows(ws =>
      ws.map(w =>
        w.id === id
          ? {
              ...w,
              maximized: false,
              minimized: false,
              x: side === 'left' ? 0 : half,
              y: 0,
              w: Math.max(MIN_W, half),
              h: lh,
              zIndex: ++zCounter.current,
            }
          : w));
    setActiveId(id);
  }, []);

  const tileWindows = useCallback(() => {
    const { w: lw, h: lh } = layerSize();
    setWindows(ws => {
      const open = ws.filter(w => !w.minimized);
      if (open.length === 0) return ws;
      const cols = Math.ceil(Math.sqrt(open.length));
      const rows = Math.ceil(open.length / cols);
      const cw = Math.max(MIN_W, Math.floor(lw / cols));
      const ch = Math.max(MIN_H, Math.floor(lh / rows));
      let i = 0;
      return ws.map(w => {
        if (w.minimized) return w;
        const col = i % cols;
        const row = Math.floor(i / cols);
        i++;
        return { ...w, maximized: false, x: col * cw, y: row * ch, w: cw, h: ch };
      });
    });
  }, []);

  return (
    <WindowManagerContext.Provider
      value={{
        windows,
        activeId,
        openWindow,
        closeWindow,
        minimizeWindow,
        toggleMaximize,
        togglePin,
        restoreWindow,
        focusWindow,
        setWindowBounds,
        setWindowTitle,
        snapWindow,
        tileWindows,
      }}
    >
      {children}
    </WindowManagerContext.Provider>
  );
};

export function useWindowManager(): WindowManagerValue {
  const ctx = useContext(WindowManagerContext);
  if (!ctx) throw new Error('useWindowManager must be used within WindowManagerProvider');
  return ctx;
}
